import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Crown, Zap, Gift, Clock, CreditCard, X, ArrowLeft, Shield, FileText, Smartphone, RefreshCw } from 'lucide-react';
import { isNativePlatform, getPlatform, getStoreProducts, purchaseProduct, finishTransaction, restorePurchases, IAP_PRODUCTS } from '../services/iapService';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPage = ({ token, currentPlan, onUpgrade, hideFree = false }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(null);
  const [storeProducts, setStoreProducts] = useState([]);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const isNative = isNativePlatform();
  const platform = getPlatform();

  useEffect(() => {
    fetchPlans();
    fetchSubscriptionDetails();

    // Load store products on native platforms
    if (isNative) {
      loadStoreProducts();
    }

    // Check for returning from Stripe (web only)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const loadStoreProducts = async () => {
    const products = await getStoreProducts();
    setStoreProducts(products);
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await axios.get(
        `${API}/subscription/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    }
  };

  // === NATIVE IAP PURCHASE ===
  const handleIAPPurchase = async (planKey) => {
    if (loading) return;
    setLoading(true);
    setLoadingPlan(planKey);

    // HARD SAFETY NET: no matter what happens in the native plugin, force-reset
    // the Processing button state after 7 seconds so the UI cannot stay stuck.
    // Apple rejected the app twice for "stuck Processing state" (Guideline 2.1b).
    const safetyReset = setTimeout(() => {
      setLoading(false);
      setLoadingPlan(null);
    }, 7000);

    try {
      // Try to purchase directly - StoreKit 2 handles product loading internally
      const transaction = await purchaseProduct(planKey);

      // Verify with our backend
      const response = await axios.post(
        `${API}/iap/verify`,
        {
          platform,
          transaction_id: transaction.id,
          product_id: IAP_PRODUCTS[planKey]?.[platform] || IAP_PRODUCTS[planKey]?.ios,
          receipt_data: transaction.receipt || null,
          purchase_token: transaction.purchaseToken || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Finish the transaction (acknowledge)
      await finishTransaction(transaction.id);

      const msg = response.data.trial_days
        ? `Your ${response.data.trial_days}-day free trial has started! You won't be charged until the trial ends.`
        : t('sub.activated');
      alert(msg);
      onUpgrade();
      fetchSubscriptionDetails();
    } catch (error) {
      console.error('IAP purchase error:', error);
      const msg = error?.response?.data?.detail || error?.message || '';
      // Don't show error for cancellations or plugin unavailability
      if (msg === 'cancelled' || msg.includes('cancel') || msg.includes('canceled')) {
        // User tapped cancel — no need for an error
      } else if (msg === 'iap_not_configured' || msg.includes('timeout')) {
        alert(
          'Subscriptions are temporarily unavailable on this device. ' +
          'Please try again in a few minutes, or visit nutrifitness.us from a web browser to subscribe. ' +
          'If the issue persists, contact support@nutrifitness.us.'
        );
      } else if (msg && msg.length > 0 && !msg.includes('not available')) {
        alert('Unable to complete purchase. Please try again, or contact support@nutrifitness.us if the issue persists.');
      }
    } finally {
      clearTimeout(safetyReset);
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  // === STRIPE PURCHASE (WEB) ===
  const handleStripePurchase = async (planKey) => {
    if (planKey === 'free' || loading) return;
    setLoading(true);
    setLoadingPlan(planKey);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { plan: planKey, origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      alert(`Checkout error: ${errorMsg}. Please try again or contact support.`);
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  // Route to the right purchase handler
  const handleUpgrade = (planKey) => {
    if (planKey === 'free') return;

    // Monthly trial goes through Stripe on web
    if (planKey === 'monthly_trial' && !isNative) {
      handleStripePurchase(planKey);
      return;
    }

    if (isNative && planKey !== 'monthly_trial') {
      handleIAPPurchase(planKey);
    } else {
      handleStripePurchase(planKey);
    }
  };

  // === RESTORE PURCHASES ===
  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      const response = await axios.post(
        `${API}/iap/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'restored') {
        alert(response.data.message);
        onUpgrade();
        fetchSubscriptionDetails();
      } else if (response.data.status === 'expired') {
        alert('Your previous subscription has expired. Please subscribe again.');
      } else {
        alert('No previous purchases found.');
      }
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore purchases. Please try again.');
    } finally {
      setRestoringPurchases(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your current period.')) {
      return;
    }

    setCancelingSubscription(true);
    try {
      const response = await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      fetchSubscriptionDetails();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setCheckingStatus(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/subscription/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === 'paid' || response.data.payment_status === 'trial_active') {
          const message = response.data.has_trial
            ? `Your ${response.data.trial_days}-day free trial has started! You won't be charged until the trial ends.`
            : t('sub.activated');
          alert(message);
          onUpgrade();
          fetchSubscriptionDetails();
          window.history.replaceState({}, document.title, window.location.pathname);
          setCheckingStatus(false);
          return;
        }

        if (response.data.status === 'expired') {
          alert('Payment session expired. Please try again.');
          setCheckingStatus(false);
          return;
        }

        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        console.error('Error checking status:', error);
        setCheckingStatus(false);
      }
    };

    poll();
  };

  const regularPlanOrder = hideFree
    ? ['monthly', 'semi_annual', 'annual']
    : ['free', 'monthly', 'semi_annual', 'annual'];
  const hasTrialPlan = plans['monthly_trial'];

  // Helper: get store price string for a plan
  const getStorePrice = (planKey) => {
    const productId = IAP_PRODUCTS[planKey]?.[platform];
    const sp = storeProducts.find((p) => p.id === productId);
    return sp?.priceString || null;
  };

  return (
    <div className="max-w-5xl mx-auto mt-4">
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Choose Your Plan</h1>
        <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
          Unlock premium features and take your nutrition tracking to the next level
        </p>
        {isNative && (
          <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <Smartphone size={12} />
            <span>{platform === 'ios' ? 'Apple' : 'Google'} In-App Purchase</span>
          </div>
        )}
      </div>

      {checkingStatus && (
        <div className="bg-orange-50 border border-[#FF6600] rounded-lg p-4 mb-6 text-center">
          <div className="font-semibold text-sm mb-1">Processing your payment...</div>
          <div className="text-gray-600 dark:text-gray-300 text-xs">Please wait while we confirm your subscription</div>
        </div>
      )}

      {/* Current Subscription Status */}
      {subscriptionDetails && subscriptionDetails.is_trialing && (
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Clock size={24} />
              <div>
                <div className="font-semibold text-sm">Free Trial Active</div>
                <div className="text-xs opacity-90">
                  Trial ends: {new Date(subscriptionDetails.trial_end_date).toLocaleDateString()}
                </div>
              </div>
            </div>
            {!subscriptionDetails.cancel_at_period_end && (
              <button
                onClick={handleCancelSubscription}
                disabled={cancelingSubscription}
                className="bg-white dark:bg-gray-800/20 hover:bg-white dark:bg-gray-800/30 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors text-xs"
              >
                <X size={14} />
                Cancel Before Trial Ends
              </button>
            )}
            {subscriptionDetails.cancel_at_period_end && (
              <div className="bg-white dark:bg-gray-800/20 px-3 py-1.5 rounded-md text-xs">
                Subscription will not renew
              </div>
            )}
          </div>
        </div>
      )}

      {/* FREE TRIAL BANNER */}
      {hasTrialPlan && currentPlan === 'free' && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 mb-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 opacity-10 hidden sm:block">
            <Gift size={80} />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={20} />
              <h2 className="text-base sm:text-lg font-bold">7-Day FREE Trial</h2>
            </div>
            <p className="text-xs sm:text-sm mb-3 opacity-95">
              Try Premium free for 7 days. Cancel anytime!
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="bg-white dark:bg-gray-800/20 rounded-full px-2 py-0.5 text-xs">
                {isNative ? (platform === 'ios' ? 'Apple Pay' : 'Google Pay') : 'Card required'}
              </span>
              <span className="bg-white dark:bg-gray-800/20 rounded-full px-2 py-0.5 text-xs">$0 today</span>
              <span className="bg-white dark:bg-gray-800/20 rounded-full px-2 py-0.5 text-xs">Cancel anytime</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => isNative ? handleIAPPurchase('monthly') : handleUpgrade('monthly_trial')}
                disabled={loading}
                className="bg-white dark:bg-gray-800 text-green-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-bold text-sm transition-colors flex items-center gap-1.5"
                data-testid="start-trial-btn"
              >
                <Zap size={14} />
                {loading && loadingPlan === 'monthly' ? 'Starting...' : 'Start Free Trial'}
              </button>
              <span className="text-white/80 text-xs">
                <span className="line-through opacity-70">{getStorePrice('monthly') || '$9.99'}</span>
                <span className="font-bold ml-1">FREE</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Regular Plans Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {regularPlanOrder.map((planKey) => {
          const plan = plans[planKey];
          if (!plan) return null;

          const isCurrentPlan = currentPlan === planKey ||
            (currentPlan === 'monthly' && planKey === 'monthly' && subscriptionDetails?.is_trialing);
          const isPremium = planKey !== 'free';
          const isPopular = planKey === 'monthly';
          const storePriceStr = getStorePrice(planKey);
          const isLoadingThis = loading && loadingPlan === planKey;

          return (
            <div
              key={planKey}
              data-testid={`plan-${planKey}`}
              className={`bg-white dark:bg-gray-800 rounded-lg p-3 border-2 relative overflow-hidden ${
                isPopular ? 'border-[#FF6600] shadow-lg pt-7' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {isPopular && (
                <div className="absolute top-0 left-0 right-0">
                  <div className="bg-[#FF6600] text-white px-2 py-1 text-[10px] font-semibold flex items-center justify-center gap-1">
                    <Zap size={10} />
                    POPULAR
                  </div>
                </div>
              )}

              {isPremium && (
                <Crown className="text-[#FF6600] mb-1" size={18} />
              )}

              <h3 className="text-sm font-bold mb-0.5 leading-tight">{plan.name}</h3>

              <div className="mb-2">
                <span className="text-xl font-bold text-[#FF6600]">
                  {storePriceStr || `$${plan.price}`}
                </span>
                {isPremium && (
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    /{planKey === 'annual' ? 'yr' : planKey === 'semi_annual' ? '6mo' : 'mo'}
                  </span>
                )}
              </div>

              {isPremium && (
                <div className="text-xs text-green-600 font-medium mb-1">7-day free trial</div>
              )}

              <div className="space-y-1 mb-3 text-xs">
                {plan.features.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-1">
                    <Check size={12} className="text-[#FF6600] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300 leading-tight">{feature}</span>
                  </div>
                ))}
                {plan.features.length > 3 && (
                  <div className="text-gray-400 text-[10px]">+{plan.features.length - 3} more</div>
                )}
              </div>

              <button
                onClick={() => handleUpgrade(planKey)}
                disabled={isCurrentPlan || loading || planKey === 'free'}
                data-testid={`select-plan-${planKey}`}
                className={`w-full py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  isCurrentPlan
                    ? 'bg-gray-200 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : isPremium
                    ? 'bg-[#FF6600] text-white hover:bg-[#E55C00]'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {isLoadingThis ? 'Processing...' : isCurrentPlan ? 'Current' : isPremium ? 'Subscribe' : 'Current'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Restore Purchases (Native Only) */}
      {isNative && currentPlan === 'free' && (
        <div className="mt-6 text-center">
          <button
            onClick={handleRestorePurchases}
            disabled={restoringPurchases}
            className="text-[#FF6600] hover:text-[#E55C00] text-sm font-medium flex items-center gap-2 mx-auto"
            data-testid="restore-purchases-btn"
          >
            <RefreshCw size={14} className={restoringPurchases ? 'animate-spin' : ''} />
            {restoringPurchases ? 'Restoring...' : 'Restore Purchases'}
          </button>
          <p className="text-gray-400 text-xs mt-1">
            Previously purchased? Tap to restore your subscription.
          </p>
        </div>
      )}

      {/* Cancel Subscription Button for Non-Trial Active Subscriptions */}
      {subscriptionDetails &&
       currentPlan !== 'free' &&
       !subscriptionDetails.is_trialing &&
       !subscriptionDetails.cancel_at_period_end && (
        <div className="mt-8 text-center">
          <button
            onClick={handleCancelSubscription}
            disabled={cancelingSubscription}
            className="text-red-500 hover:text-red-600 underline text-sm"
            data-testid="cancel-subscription-btn"
          >
            {cancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
          </button>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
            You will keep access until the end of your current billing period
          </p>
        </div>
      )}

      {/* Trust Badges & Footer Section */}
      <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-center items-center gap-8 flex-wrap mb-8">
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 px-4 py-3 rounded-lg">
            <CreditCard size={20} className="text-[#FF6600]" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {isNative ? `Secure ${platform === 'ios' ? 'Apple' : 'Google'} Payment` : 'Secure payment via Stripe'}
            </span>
          </div>
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 px-4 py-3 rounded-lg">
            <Check size={20} className="text-green-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 px-4 py-3 rounded-lg">
            <Clock size={20} className="text-blue-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">7-day free trial</span>
          </div>
        </div>

        <div className="text-center space-y-4">
          <div className="flex justify-center items-center gap-6 text-sm text-gray-600 dark:text-gray-300">
            <button
              onClick={() => setShowLegalModal('privacy')}
              className="hover:text-[#FF6600] transition-colors"
            >
              Privacy Policy
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => setShowLegalModal('terms')}
              className="hover:text-[#FF6600] transition-colors"
            >
              Terms of Service
            </button>
            <span className="text-gray-300">|</span>
            <a href="mailto:support@nutrifitness.us" className="hover:text-[#FF6600] transition-colors">Contact Support</a>
          </div>

          <div className="flex justify-center items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>Trusted by 50K+ users</span>
            <span>•</span>
            <span>4.8 Rating</span>
            <span>•</span>
            <span>256-bit SSL Encryption</span>
          </div>

          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Nutrifitness. All rights reserved.
            {isNative && platform === 'ios' && (
              <span className="block mt-1">
                Payment will be charged to your Apple ID account. Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period.
              </span>
            )}
            {isNative && platform === 'android' && (
              <span className="block mt-1">
                Payment will be charged to your Google Play account. Subscription automatically renews unless cancelled at least 24 hours before the end of the current period.
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-black p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                {showLegalModal === 'privacy' ? (
                  <Shield size={24} className="text-[#FF6600]" />
                ) : (
                  <FileText size={24} className="text-[#FF6600]" />
                )}
                <h2 className="font-bold text-lg">
                  {showLegalModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
              </div>
              <button
                onClick={() => setShowLegalModal(null)}
                className="text-white hover:text-[#FF6600] transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {showLegalModal === 'privacy' ? (
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Last updated: January 2, 2026</p>
                  <h3 className="text-lg font-bold mb-2">1. Introduction</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Welcome to NutriFitness AI ("we", "us", or "our"). We are committed to protecting your personal information and your right to privacy.</p>
                  <h3 className="text-lg font-bold mb-2">2. Information We Collect</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-2">We collect:</p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Name and email address</li>
                    <li>Profile information (height, weight, fitness goals)</li>
                    <li>Food photos and meal logs</li>
                    <li>Exercise and workout data</li>
                    <li>Payment information (processed via Stripe, Apple Pay, or Google Pay)</li>
                  </ul>
                  <h3 className="text-lg font-bold mb-2">3. How We Use Your Information</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">We use your information to provide nutrition tracking services, analyze food photos using AI, track your fitness progress, and process payments.</p>
                  <h3 className="text-lg font-bold mb-2">4. Data Security</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">We implement industry-standard security measures including encryption, secure servers, and regular security audits.</p>
                  <h3 className="text-lg font-bold mb-2">5. Contact Us</h3>
                  <p className="text-gray-600 dark:text-gray-300">Email: support@nutrifitness.us</p>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Last updated: March 13, 2026</p>
                  <h3 className="text-lg font-bold mb-2">1. Acceptance of Terms</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">By accessing or using NutriFitness AI, you agree to be bound by these Terms of Service.</p>
                  <h3 className="text-lg font-bold mb-2">2. Service Description</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">NutriFitness AI provides AI-powered nutrition tracking, meal logging, exercise tracking, and personalized health recommendations.</p>
                  <h3 className="text-lg font-bold mb-2">3. User Accounts</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
                  <h3 className="text-lg font-bold mb-2">4. Subscription & Payments</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Premium features require a paid subscription. Payments are processed via Stripe (web), Apple In-App Purchase (iOS), or Google Play Billing (Android). All subscriptions include a 7-day free trial. You may cancel at any time.</p>
                  <h3 className="text-lg font-bold mb-2">5. Auto-Renewal</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period. You can manage and cancel subscriptions through your device settings or the app.</p>
                  <h3 className="text-lg font-bold mb-2">6. Health Disclaimer</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">NutriFitness AI is not a substitute for professional medical advice. Always consult with healthcare professionals for health-related decisions.</p>
                  <h3 className="text-lg font-bold mb-2">7. Contact Us</h3>
                  <p className="text-gray-600 dark:text-gray-300">Email: support@nutrifitness.us</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => setShowLegalModal(null)}
                className="w-full bg-[#FF6600] hover:bg-[#E55C00] text-white py-3 rounded-xl font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
